import React, { useEffect, useCallback, useState, useMemo } from 'react';

import { Form, Input, Button, Box } from '@kufox/mui';

import { useDispatch, useSelector } from 'react-redux';

import { REGEXP, useFormValidateByLang } from '../../common/tools';

import InputSendCode from '../../components/InputSendCode';

import { LOCAL_PHONE_INPUT_KEY, NAMESPACE } from '../constants';

import { useToast, useLang } from '../../hookTool';
import VoiceCode from '../../components/voiceCode';

import PhoneAreaSelector from '../../components/PhoneAreaSelector';

const { FormItem } = Form;

const ForgetPhonePwd = () => {
  const [form] = Form.useForm();
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [disable, setDisable] = useState(false);

  const dispatch = useDispatch();
  const toast = useToast();
  const {
    t,
    i18n: { language },
  } = useLang();

  const isCn = language === 'zh_CN';

  const { isCount, loading, SMS_countTime, countryCodes, bizType, sendChannel = '' } = useSelector(
    (state) => state[NAMESPACE],
  );

  useEffect(() => {
    dispatch({
      type: `${NAMESPACE}/getCountryCodes`,
    });
    return () => {};
  }, []);

  const handleSendCode = useCallback(() => {
    form.validateFields(['phone', 'countryCode']).then(({ phone, countryCode }) => {
      dispatch({
        type: `${NAMESPACE}/sendVerifyCode`,
        payload: {
          phone,
          countryCode,
          bizType,
          toast,
          sendChannel: 'SMS',
        },
      });
    });
  }, []);

  const onSendVoice = useCallback(() => {
    return dispatch({
      type: `${NAMESPACE}/sendVerifyCode`,
      payload: {
        phone,
        countryCode,
        bizType,
        toast,
        sendChannel: 'VOICE',
      },
    });
  }, [phone, countryCode, toast, dispatch, setDisable]);

  const handleSubmit = useCallback(
    async (e) => {
      e && e.preventDefault();
      let formValue = null;
      // 校验表单值
      form.validateFields().then((values) => {
        formValue = values;
        const { phone, countryCode, code, password } = formValue;
        const payload = {
          phone,
          countryCode,
          code,
          toast,
          password,
          sendChannel,
        };
        dispatch({
          type: `${NAMESPACE}/verifyCode`,
          payload: { ...payload, bizType },
        });
      });
    },
    [sendChannel],
  );

  const validateForVoice = useCallback(() => {
    return new Promise((resolve, reject) => {
      form
        .validateFields(['phone', 'countryCode'])
        .then(({ phone, countryCode }) => {
          setPhone(phone);
          setCountryCode(countryCode);
          resolve();
        })
        .catch(() => {
          reject();
        });
    });
  }, [form, setPhone, setCountryCode]);

  const offDisabled = useCallback(() => {
    setDisable(false);
    dispatch({
      type: `${NAMESPACE}/update`,
      payload: {
        SMS_countTime: { time: 0 },
      },
    });
  }, [dispatch]);

  useEffect(() => {
    if (SMS_countTime && SMS_countTime.time > 0) {
      setDisable(true);
    }
  }, [SMS_countTime]);

  const initCountryCode = useMemo(() => {
    const langCode = language ? language.split('_')[1] : 'US';
    // 葡萄牙语，则默认展示+55（巴西区号，我们不支持葡萄牙注册）
    if (langCode === 'PT') {
      return '55';
    }
    const curCountry = countryCodes.find((c) => c.code === langCode);
    if (countryCodes && curCountry) {
      return curCountry.mobileCode;
    }
    return '1';
  }, [language, countryCodes]);

  const commonRule = [{ required: true, whitespace: true, message: t('form.required') }];

  const addonBefore = (
    <FormItem noStyle initialValue={initCountryCode} name="countryCode">
      <PhoneAreaSelector countries={countryCodes} isCn={isCn} language={language} checkForbidden />
    </FormItem>
  );

  useFormValidateByLang(form, language);

  return (
    <Form form={form}>
      <FormItem
        label={t('phone')}
        name="phone"
        initialValue={localStorage.getItem(LOCAL_PHONE_INPUT_KEY) || ''}
        rules={[
          ...commonRule,
          {
            pattern: REGEXP.phone,
            message: t('form.phone.error'),
          },
        ]}
        validateTrigger={['onBlur', 'onSubmit']}
      >
        <InputSendCode
          onSendCode={handleSendCode}
          isCount={isCount}
          loading={loading}
          disabled={disable}
          size="large"
          countTime={sendChannel && sendChannel === 'SMS' ? SMS_countTime : null}
          countTimeOver={offDisabled}
          placeholder={t('phone')}
          addonBefore={addonBefore}
        />
      </FormItem>
      <FormItem
        label={t('vc.phone')}
        name="code"
        rules={commonRule}
        validateTrigger={['onBlur', 'onSubmit']}
      >
        <Input size="large" />
      </FormItem>
      <Button mt={34} type="primary" fullWidth size="large" onClick={handleSubmit}>
        {t('forget.btn')}
      </Button>
      <Box display="flex" justifyContent="flex-end">
        <VoiceCode
          validateFunc={validateForVoice}
          phone={phone}
          countryCode={form.getFieldValue('countryCode') || ''}
          countTime={sendChannel && sendChannel === 'VOICE' ? SMS_countTime : null}
          loading={loading}
          disable={disable}
          onTimeOver={offDisabled}
          onSend={onSendVoice}
        />
      </Box>
    </Form>
  );
};

export default ForgetPhonePwd;
